import { z } from "zod";
import { KPI_STATUSES, PROJECT_HEALTHS, SOLVE_PHASES, TASK_BUCKETS, VSM_PLAN_STATUSES, VSM_ROLES, parseDatasetContractRef, } from "@playbookai/types";
import { idString, IsoDate, MediumText, ShortText } from "./common.js";
import { PersonalKaizenListInputSchema, VsmKaizenListInputSchema, } from "./dashboardKaizen.js";
import { SolveActionsInputSchema, SolveActionsOutputSchema, } from "./solveActions.js";
import { STRATEGIC_HEALTH_ICONS } from "./strategicHealth.js";
/**
 * Dashboard dataset contracts (ADR-0054) — the typed read contracts widgets
 * bind to through a persisted `WidgetDataConnection`. Pure Zod, shared by the
 * playbook-be provider registry (input validation at connection writes, output
 * validation before a result leaves the resolver) and the frontend hosts
 * (inferred view types).
 *
 * Inputs are dataset SELECTION only (e.g. `kpiId`): scope/identity — tenant,
 * actor, dashboard subject — always derives from the server-side
 * `DataProviderContext`, never from parameters. Outputs are semantic
 * projections, never raw domain rows.
 */
// ---------- shared fragments ----------
/** KPI projection shared by the KPI-family datasets. */
export const KpiSnapshotSchema = z
    .object({
    id: idString,
    name: ShortText,
    /** Display value, e.g. "91.4%". */
    value: ShortText,
    /** Numeric form of `value` for sorting/sparklines. */
    numeric: z.number(),
    unit: ShortText.optional(),
    status: z.enum(KPI_STATUSES),
    /** Display delta, e.g. "−2.1pp"; empty when unknown. */
    delta: ShortText,
    deltaNumeric: z.number(),
    inverse: z.boolean().optional(),
    series: z.array(z.number()),
    threshold: ShortText.optional(),
    commentary: MediumText.optional(),
})
    .strict();
export const LamaConfidenceSchema = z.enum(["low", "medium", "high"]);
const EmptyInputSchema = z.object({}).strict();
// ---------- kpi provider ----------
export const KpiSnapshotInputSchema = z
    .object({ kpiId: idString.min(1) })
    .strict();
/**
 * Product data limit for distinct active KPIs related to one VSM.
 *
 * This is also the `kpi/kpi-list@1` dataset bound, so creation, dashboard
 * validation, remediation, and UI messaging can move together if product
 * raises the limit later.
 */
export const MAX_VSM_KPIS = 24;
export const KpiListInputSchema = z
    .object({ kpiIds: z.array(idString).max(MAX_VSM_KPIS) })
    .strict();
export const KpiListOutputSchema = z
    .object({ kpis: z.array(KpiSnapshotSchema) })
    .strict();
// ---------- solve provider ----------
export const ProjectPulseOutputSchema = z
    .object({
    projects: z.array(z
        .object({
        id: idString,
        name: ShortText,
        health: z.enum(PROJECT_HEALTHS),
        kpiLine: ShortText,
        pulse: ShortText,
    })
        .strict()),
})
    .strict();
export const MyTasksOutputSchema = z
    .object({
    tasks: z.array(z
        .object({
        id: idString,
        title: ShortText,
        due: ShortText,
        urgent: z.boolean(),
        why: MediumText,
        bucket: z.enum(TASK_BUCKETS),
    })
        .strict()),
})
    .strict();
/**
 * Bounded Personal Home doorway into SOLVE. The provider applies membership,
 * open-status, recency, and `take: 5` server-side; the client never paginates
 * or truncates a larger domain response inside the widget.
 */
export const MyActiveSolvesOutputSchema = z
    .object({
    solves: z
        .array(z
        .object({
        id: idString,
        title: ShortText,
        status: z.enum(["Draft", "Active", "Verified"]),
        currentPhase: z.enum(SOLVE_PHASES),
    })
        .strict())
        .max(5),
})
    .strict();
// ---------- vsm navigation + kaizen provider ----------
export const MyVisibleVsmsOutputSchema = z
    .object({
    vsms: z
        .array(z
        .object({
        id: idString,
        name: ShortText,
        lifecycle: z.enum(["DRAFT", "ACTIVE"]),
        role: z.enum(VSM_ROLES),
        plant: ShortText.nullable(),
        productFamily: ShortText.nullable(),
    })
        .strict())
        .max(5),
})
    .strict();
const WEB_URL_MESSAGE = {
    message: "url must be an absolute http(s) address",
};
/**
 * Absolute `http:`/`https:` only.
 *
 * A relative path is refused too: a register cell renders this as an EXTERNAL
 * link and has no base to resolve one against, so accepting it would silently
 * produce a link back into our own app.
 */
function isWebUrl(value) {
    try {
        const { protocol } = new URL(value);
        return protocol === "http:" || protocol === "https:";
    }
    catch {
        return false;
    }
}
/**
 * A Kaizen's external work, summarised for ONE register row (`@3`).
 *
 * ONE nullable object rather than six flat columns, because the whole thing is
 * one fact — "this Kaizen has been sent somewhere" — and a row that has not
 * been sent anywhere should be `null`, not six zeros a reader has to interpret.
 *
 * `key` and `url` name the single link worth linking to, and are BOTH null when
 * there is more than one: a register cell has room for one address, and picking
 * one of several silently would make the others invisible. The counts still
 * describe the whole set, so a reader sees "3 linked" and follows the Kaizen to
 * the surface that lists them.
 *
 * The caps are load-bearing, and the number was MEASURED rather than chosen.
 * A dataset result is bounded by `maxResultBytes` (100_000) and
 * `RESULT_TOO_LARGE` is NON-RETRYABLE, so an oversized payload permanently
 * breaks a saved dashboard slot with no way back. Serializing 50 rows at each
 * schema's own worst case:
 *
 * | shape                    | bytes   |
 * | ------------------------ | ------- |
 * | `@2` row (no linkedWork) |  51,560 |
 * | `@3` with a 1024 url     | 119,360 |
 * | `@3` with a 512 url      |  93,760 |
 *
 * So 1024 would have put the WORST CASE over budget — under it in practice,
 * which is the shape of bug that ships. 512 keeps the worst case inside it.
 *
 * The caps are also not self-enforcing: `link_external_work_refs.url` and
 * `.external_key` are unbounded Postgres text, so a provider CAN return more
 * than this. The producer drops an over-long value rather than truncating it —
 * a truncated URL is a link to nowhere and a truncated key names a different
 * issue, both worse than saying nothing.
 */
export const KaizenLinkedWorkSchema = z
    .object({
    total: z.number().int().min(0),
    open: z.number().int().min(0),
    done: z.number().int().min(0),
    unavailable: z.number().int().min(0),
    /** The provider's own key, when exactly one link makes it unambiguous. */
    key: ShortText.nullable(),
    /**
     * An absolute `http:`/`https:` address, and the CONTRACT says so.
     *
     * The producer already drops anything else, and the design system refuses
     * to render a non-web scheme in an `href` — but a URL that reached us from
     * a connector's response is DATA, and `javascript:` in an `href` executes
     * on click. Stating it here means a future producer that skipped the
     * sanitiser fails loudly at the boundary instead of shipping an executable
     * link to every reader of the register.
     */
    url: z.string().max(512).refine(isWebUrl, WEB_URL_MESSAGE).nullable(),
})
    .strict();
/**
 * One row of the Kaizen register (`@3`).
 *
 * Two-artifact model: a Kaizen burst is an OPPORTUNITY and carries no lifecycle,
 * so `status` and `targetDate` are gone from the row as well as from the shape.
 * Execution state comes from the Implementation Plan row the Kaizen became at
 * promotion — the Kaizen Newspaper — joined on the promotion key. `promoted`
 * comes from the system-owned `inImplementationPlan` flag rather than from
 * plan-row presence, so there is one source of truth for the stage: if the flag
 * is set but the plan row was deleted in the editor, the row reads promoted with
 * no status or due date rather than silently reverting.
 *
 * `targetCondition` went too: it was fetched on every read and never rendered.
 */
export const KaizenRowSchema = z
    .object({
    id: idString,
    title: ShortText,
    vsmId: idString,
    vsmName: ShortText,
    ownerName: ShortText.nullable(),
    ownerUserId: z.string().nullable(),
    /** From the system-owned `inImplementationPlan` flag, not row presence. */
    promoted: z.boolean(),
    planStatus: z.enum(VSM_PLAN_STATUSES).nullable(),
    planDue: z.string().nullable(),
    /** Server-derived, so the badge cannot disagree with the `dueState` filter. */
    isOverdue: z.boolean(),
    /**
     * `@3`. Null when nothing has been sent anywhere — an honest absence, never
     * a zeroed summary a reader has to recognise as empty.
     */
    linkedWork: KaizenLinkedWorkSchema.nullable(),
})
    .strict();
export const KaizenListOutputSchema = z
    .object({ rows: z.array(KaizenRowSchema).max(50) })
    .strict();
/**
 * Kaizen health (`@2`). The counts follow the Implementation Plan's vocabulary,
 * because that is where execution state now lives.
 *
 * `atRisk` gains its own count instead of being folded into "in progress", where
 * the one status a reader most needs to see would have disappeared. `noDueDate`
 * is gone — promotion demands a due date, so it read zero unless someone cleared
 * one by hand — and `notPromoted` takes its place, because the register's row cap
 * means the size of the unactioned pile cannot be counted by eye.
 *
 * `unassigned` counts PROMOTED work only: an unowned opportunity is normal, an
 * unowned commitment is the signal.
 */
export const KaizenHealthOutputSchema = z
    .object({
    overall: z.enum(["critical", "watch", "healthy", "neutral"]),
    onTrack: z.number().int().min(0),
    atRisk: z.number().int().min(0),
    blocked: z.number().int().min(0),
    overdue: z.number().int().min(0),
    unassigned: z.number().int().min(0),
    done: z.number().int().min(0),
    notPromoted: z.number().int().min(0),
})
    .strict();
// ---------- hoshin provider ----------
export const StrategicHealthOutputSchema = z
    .object({
    stats: z.array(z
        .object({
        label: ShortText,
        value: ShortText,
        trend: ShortText,
        status: z.enum(KPI_STATUSES),
        iconName: z.enum(STRATEGIC_HEALTH_ICONS).optional(),
    })
        .strict()),
})
    .strict();
// ---------- player-ai provider ----------
export const DailyBriefOutputSchema = z
    .object({
    headline: ShortText,
    narrative: MediumText.optional(),
    highlights: z.array(z.object({ status: z.enum(KPI_STATUSES), text: MediumText }).strict()),
})
    .strict();
export const CoachingNudgeOutputSchema = z
    .object({
    nudges: z.array(z
        .object({ id: idString, text: MediumText, askPrompt: MediumText })
        .strict()),
})
    .strict();
// ---------- personal-state provider ----------
/**
 * The authenticated actor's current-day mood state. Always actor-scoped
 * server-side — there is no input that could select another user.
 *
 * `score`/`note`/`checkinDate`/`updatedAt` describe a CHECK-IN and are absent
 * together when none is logged. `hiddenToday` and `aiAccessEnabled` describe
 * the widget and the permission, and exist whether or not a check-in does —
 * which is exactly why they could not live inside the check-in object.
 *
 * The five new-or-relaxed fields are OPTIONAL and the ref stays `@1` ON
 * PURPOSE. `dashboard-resolve.service.ts` gates on the PERSISTED
 * `contract_version`, so registering only `@2` would push every saved Mood
 * Check slot into `DASHBOARD_UNSUPPORTED_CONTRACT_VERSION` until an editor ran
 * a connection migration — for a strictly additive change no consumer can be
 * hurt by. Optional keeps both rollout orders safe: an old resolver omits the
 * keys and the widget falls back to "visible, no AI access".
 */
export const MoodTodayOutputSchema = z
    .object({
    score: z.number().int().min(1).max(5).optional(),
    note: z.string().max(80).nullable().optional(),
    /** ISO date (YYYY-MM-DD) of the check-in day, server-derived. */
    checkinDate: IsoDate.optional(),
    updatedAt: z.string().datetime({ offset: true }).optional(),
    /** Dismissed for the actor's current local day (server-derived). */
    hiddenToday: z.boolean().optional(),
    /** Player AI may currently read this actor's mood history. */
    aiAccessEnabled: z.boolean().optional(),
    /**
     * The actor's CURRENT local day and the zone it was derived in (PD1).
     *
     * Carried so a continuously-open Home can refresh at the exact local
     * midnight rather than poll for it — the dismissal expires at a boundary
     * the SERVER owns, and a client that guessed it from the browser's zone
     * would be reintroducing exactly the guess PD1 removed.
     */
    today: IsoDate.optional(),
    timeZone: z
        .string()
        .min(1)
        .max(100)
        .refine((value) => {
        try {
            new Intl.DateTimeFormat("en", { timeZone: value });
            return true;
        }
        catch {
            return false;
        }
    }, "timeZone must be a valid IANA time zone")
        .optional(),
})
    .strict();
// ---------- vsm provider ----------
/** KPIs measurement-linked to the subject VSM (`vsm_kpi_links`). */
export const VsmLinkedKpisOutputSchema = KpiListOutputSchema;
export const VsmLinkedKpiInputSchema = KpiSnapshotInputSchema;
export const VsmLinkedKpiOutputSchema = KpiSnapshotSchema;
// ---------- lama provider (eight report datasets) ----------
/**
 * Min-N suppression, on the wire (plan §15.3). An ANONYMOUS campaign whose
 * latest wave has fewer than `min_respondents` submitted respondents publishes
 * NO scores — and that is a different fact from "nobody has answered yet".
 * Every scored LAMA dataset therefore carries this flag rather than collapsing
 * to a bare empty, so a widget can say "withheld to protect anonymity" instead
 * of "no data". Participation counts are unaffected: they are the
 * non-identifying denominator the progress API already exposes.
 */
const suppressedFlag = z.boolean();
export const LamaReportOverviewOutputSchema = z
    .object({
    campaignName: ShortText,
    mode: z.enum(["baseline", "self", "inflow"]),
    cadence: ShortText,
    scopeLabel: ShortText,
    /**
     * Where the assessment stands, derived per resolve — nothing stores it.
     * `collecting` (active, no wave closed yet), `ongoing` (a wave has closed and
     * the campaign runs on), `closed` (terminal).
     *
     * REPLACED `status: draft | published`, which could only ever be `draft`
     * because report publication is unbuilt. The report LIBRARY row carries the
     * same value from the same two facts (campaign status, any closed wave), so
     * the detail page and the library cannot disagree.
     *
     * Retiring `status` is recorded in ADR-0054's PB-4942 amendment, with the
     * reasoning for keeping this dataset at `@1` rather than versioning it — the
     * persisted `contract_version` gates PARAMETER compatibility, and both gates
     * in `dashboard-resolve.service.ts` would error every existing report if the
     * registered version moved without a paired row rewrite.
     */
    lifecycle: z.enum(["baseline", "collecting", "ongoing", "closed"]),
    waveCount: z.number().int().min(0),
    latestWaveLabel: ShortText,
    latestWaveCode: ShortText,
    /**
     * True when the latest wave is still OPEN, so every score on this payload
     * is a live recomputation rather than a frozen closed-wave figure
     * (plan §13.4). Closed waves are immutable; an open one moves.
     */
    latestWaveLive: z.boolean(),
    /**
     * Overall maturity of the latest wave on the 0–5 wire scale; null when
     * suppressed/unscored. Since the 2026-09-10 ADR-0027 amendment this is the
     * v3 EQUAL-PILLAR figure (percent / 20) — the same arithmetic as
     * `lama/maturity-hierarchy@1` — never the pre-v3 equal-category mean, so
     * one report cannot carry two overalls. `waveCount: 0` (a baseline-only
     * campaign) carries null here and zero participation/coverage.
     */
    overall: z.number().nullable(),
    /** Delta vs the previous wave (v3, 0–5 scale); null on a single-wave report. */
    delta: z.number().nullable(),
    /**
     * Delta vs the FIRST wave (v3, 0–5 scale); null when the first wave IS the
     * latest. Named before the facilitator baseline existed and never means it:
     * that comparison lives on `lama/maturity-hierarchy@1`.
     */
    sinceBaseline: z.number().nullable(),
    /** Whole-percent participation of the latest wave. */
    participation: z.number().int().min(0).max(100),
    /** 0–1 coverage of the latest wave. */
    coverage: z.number().min(0).max(1),
    confidence: LamaConfidenceSchema,
    /** See {@link suppressedFlag}. ALWAYS FALSE since the respondent-count
     *  threshold was removed (2026-09-15); kept for wire-shape stability. */
    suppressed: suppressedFlag,
    /**
     * PARTICIPATION IN PEOPLE — "X of N people have responded" (2026-09-15,
     * Task 3 of `docs/planning/lama-experience-simplification-plan.md`).
     *
     * They REPLACED `minRespondents`, which named the anonymity floor the copy
     * used to quote ("Scores appear once at least 4 people respond") and which
     * no longer exists. Both count DISTINCT PEOPLE in the LATEST wave and
     * neither is derivable from `participation` or `coverage`, which are
     * completion rates over ASSIGNMENT ROWS — an assignment is person × release,
     * so the two units genuinely differ.
     *
     * `respondedPeople`: people with at least one SUBMITTED assignment in that
     * wave. `totalPeople`: the wave's FROZEN participant set, counted distinct.
     *
     * OPTIONAL, so this is additive on a `.strict()` schema and both rollout
     * orders stay safe — an older resolver omits them and the widget falls back
     * to saying nothing about people rather than to a fabricated zero. Zero from
     * a CURRENT resolver is a real zero, which is why absence and zero must not
     * be collapsed at the consumer.
     */
    respondedPeople: z.number().int().min(0).optional(),
    totalPeople: z.number().int().min(0).optional(),
    /**
     * How the campaign collects answers (ADR-0027 §5). The report's privacy
     * statement is HOST CHROME and must be true of THIS campaign, and without
     * this field it could not be: the host had only `minRespondents` and
     * `suppressed`, so it said "Responses are anonymous" on every report —
     * including an `attributed` one, where no floor was ever applied. Two false
     * claims in one sentence. It is now the ONLY privacy fact the statement has,
     * which is the right shape: anonymity is about names, and names are the one
     * thing it still governs.
     *
     * OPTIONAL, so this is strictly additive on a `.strict()` schema and both
     * rollout orders stay safe: an older resolver omits the key and the host
     * falls back to a conditional wording that asserts nothing about this
     * campaign. Never widen it to a free string — the column's CHECK admits
     * exactly these two.
     */
    anonymity: z.enum(["attributed", "anonymous"]).optional(),
})
    .strict();
/**
 * Radar rows. The under-three-pillars state is represented by the DATA (fewer
 * than three rows) and rendered as the widget's bounded fallback, exactly like
 * the current report page's `radarData.length >= 3` gate.
 */
export const LamaMaturityProfileOutputSchema = z
    .object({
    /**
     * One axis per PILLAR of the version (per category on a version without
     * pillars), from the latest wave's v3 hierarchy — the same rows the
     * hierarchy sections draw, so two radars on one report agree. An axis with
     * no score is absent. `current`/`previous` are the 0–5 wire scale
     * (percent / 20).
     */
    pillars: z.array(z
        .object({
        pillar: ShortText,
        current: z.number(),
        previous: z.number().nullable(),
        /**
         * Authored pillar target. ALWAYS null today, deliberately: the old
         * report page read a target from a FIXTURE, and no authoring surface
         * or column carries one — plan dependency PD5 owns supplying it. The
         * field stays in the contract because §13.3 lists it and the radar
         * already renders the overlay the moment a real source exists; it is
         * never fabricated from the data.
         */
        target: z.number().nullable(),
    })
        .strict()),
    /** See {@link suppressedFlag}. `pillars` is empty when true. */
    suppressed: suppressedFlag,
})
    .strict();
export const LamaMaturityTrendOutputSchema = z
    .object({
    points: z.array(z
        .object({
        code: ShortText,
        label: ShortText,
        date: IsoDate,
        /** The wave's v3 equal-pillar overall on the 0–5 wire scale. */
        overall: z.number(),
        /**
         * True for the OPEN wave's point — a live recomputation, not a
         * frozen closed-wave value (§13.4). The widget marks it so a moving
         * number is never read as history.
         */
        live: z.boolean(),
    })
        .strict()),
    /** True when the latest wave's score is withheld by the min-N policy. */
    suppressed: suppressedFlag,
})
    .strict();
export const LamaParticipationTrendOutputSchema = z
    .object({
    points: z.array(z
        .object({
        code: ShortText,
        label: ShortText,
        date: IsoDate,
        participation: z.number().int().min(0).max(100),
        /**
         * The numerator and denominator `participation` is rounded from
         * (PB-191 Task 20). Submitted and total ASSIGNMENT rows — a completion
         * rate, not a head count.
         *
         * OPTIONAL, AND NOT A VERSION BUMP. Adding required fields would break
         * a rollout in one direction and bumping to `@2` would need a template
         * migration to move every persisted `lama/participation-trend@1`
         * connection — for a strictly additive change no consumer can be hurt
         * by. Optional keeps BOTH rollout orders safe: an old resolver serving
         * a new widget omits them, and the widget shows the percentage alone.
         *
         * A percentage without a denominator is the honesty problem this fixes:
         * "50%" reads the same whether it is 1 of 2 or 40 of 80, and only one
         * of those means anything.
         */
        submitted: z.number().int().min(0).optional(),
        total: z.number().int().min(0).optional(),
        /** The band `coverage` falls in, so the chart can say how much weight
         *  a bar carries. Optional for the same rollout reason. */
        confidence: LamaConfidenceSchema.optional(),
        /** True for the OPEN wave's bar — still accruing (§13.4). */
        live: z.boolean(),
    })
        .strict()),
})
    .strict();
/**
 * `lama/maturity-hierarchy@1` (2026-09-10 ADR-0027 amendment, algorithm v3):
 * the Pillar → Category → Question hierarchy in PERCENT for BOTH report
 * sources — the finalised baseline and the latest wave — plus the comparison
 * precomputed on their shared scope.
 *
 * Every percent is FULL PRECISION (0–100, or `null` when unassessed — never
 * zero for "not entered"); the widget rounds to a whole percent at render and
 * nothing downstream may feed a rounded value back into a mean. Coverage
 * counts travel with every level so a mean over a subset is always shown
 * beside the size of the subset ("4 of 6 pillars assessed").
 */
const LamaHierarchyQuestionSchema = z
    .object({
    questionId: z.string().min(1),
    /** Truncated by the provider; the drill-down's row label. */
    prompt: z.string().max(200),
    /** 0–5, or null when unassessed. */
    score: z.number().nullable(),
    percent: z.number().nullable(),
})
    .strict();
const LamaHierarchyCategorySchema = z
    .object({
    categoryId: z.string().min(1),
    label: ShortText,
    pillarId: z.string().min(1).nullable(),
    percent: z.number().nullable(),
    /** Which input produced `percent` (baseline: direct or questions). */
    method: z.enum(["direct", "questions"]).nullable(),
    assessedQuestionCount: z.number().int().nonnegative(),
    questionCount: z.number().int().nonnegative(),
    questions: z.array(LamaHierarchyQuestionSchema),
})
    .strict();
const LamaHierarchyPillarSchema = z
    .object({
    pillarId: z.string().min(1),
    label: ShortText,
    percent: z.number().nullable(),
    assessedCategoryCount: z.number().int().nonnegative(),
    categoryCount: z.number().int().nonnegative(),
    categories: z.array(LamaHierarchyCategorySchema),
})
    .strict();
export const LamaHierarchyResultSchema = z
    .object({
    /** No pillar in scope: overall is the mean of assessed categories. */
    flat: z.boolean(),
    overallPercent: z.number().nullable(),
    assessedPillarCount: z.number().int().nonnegative(),
    pillarCount: z.number().int().nonnegative(),
    assessedCategoryCount: z.number().int().nonnegative(),
    categoryCount: z.number().int().nonnegative(),
    pillars: z.array(LamaHierarchyPillarSchema),
    /** Categories outside every pillar — all of them on a flat version. */
    ungroupedCategories: z.array(LamaHierarchyCategorySchema),
})
    .strict();
/**
 * The baseline → responses comparison, RECOMPUTED ON THE MATCHED ASSESSED
 * SCOPE (review note 9): a category counts only when BOTH sides assessed it,
 * and inside a question-scored category only the questions scored on both
 * sides enter either side's mean. "Same pinned version" is not "same assessed
 * scope" — a baseline that judged one category and a wave that answered six
 * would otherwise report five categories of invented improvement. So every
 * level carries its comparable-vs-total counts, a direct baseline judgement
 * is labelled as such and carries NO question rows (nothing per question was
 * ever entered to compare), and a null delta means "not comparable", never 0.
 */
const LamaHierarchyComparisonQuestionSchema = z
    .object({
    questionId: z.string().min(1),
    prompt: z.string().max(200),
    baselinePercent: z.number(),
    responsesPercent: z.number(),
    /** Responses minus baseline, in points. */
    deltaPoints: z.number(),
})
    .strict();
const LamaHierarchyComparisonCategorySchema = z
    .object({
    categoryId: z.string().min(1),
    label: ShortText,
    pillarId: z.string().min(1).nullable(),
    /** How the baseline side was entered; `direct` carries no question rows. */
    baselineMethod: z.enum(["direct", "questions"]),
    /** Both recomputed on the matched questions (or the direct judgement). */
    baselinePercent: z.number().nullable(),
    responsesPercent: z.number().nullable(),
    /** Null when the category is not comparable — never a delta against nothing. */
    deltaPoints: z.number().nullable(),
    /** Questions assessed on BOTH sides; always 0 for a direct baseline. */
    comparableQuestionCount: z.number().int().nonnegative(),
    questionCount: z.number().int().nonnegative(),
    questions: z.array(LamaHierarchyComparisonQuestionSchema),
})
    .strict();
const LamaHierarchyComparisonPillarSchema = z
    .object({
    pillarId: z.string().min(1),
    label: ShortText,
    baselinePercent: z.number().nullable(),
    responsesPercent: z.number().nullable(),
    deltaPoints: z.number().nullable(),
    comparableCategoryCount: z.number().int().nonnegative(),
    categoryCount: z.number().int().nonnegative(),
    categories: z.array(LamaHierarchyComparisonCategorySchema),
})
    .strict();
export const LamaHierarchyComparisonSchema = z
    .object({
    flat: z.boolean(),
    baselinePercent: z.number().nullable(),
    responsesPercent: z.number().nullable(),
    deltaPoints: z.number().nullable(),
    comparablePillarCount: z.number().int().nonnegative(),
    pillarCount: z.number().int().nonnegative(),
    comparableCategoryCount: z.number().int().nonnegative(),
    categoryCount: z.number().int().nonnegative(),
    pillars: z.array(LamaHierarchyComparisonPillarSchema),
    ungroupedCategories: z.array(LamaHierarchyComparisonCategorySchema),
})
    .strict();
export const LamaMaturityHierarchyOutputSchema = z
    .object({
    campaignName: ShortText,
    scope: z
        .object({
        flat: z.boolean(),
        pillars: z.array(z.object({ id: z.string().min(1), label: ShortText }).strict()),
        categories: z.array(z
            .object({
            id: z.string().min(1),
            label: ShortText,
            pillarId: z.string().min(1).nullable(),
        })
            .strict()),
    })
        .strict(),
    /** The latest FINALISED baseline revision, or null when none is. */
    baseline: z
        .object({
        revision: z.number().int().positive(),
        /** `YYYY-MM-DD` — the date the results describe. */
        assessmentDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        /** An instant (ISO 8601), unlike `assessmentDate`, which is a calendar date. */
        finalisedAt: z.string().datetime({ offset: true }),
        result: LamaHierarchyResultSchema,
    })
        .strict()
        .nullable(),
    /** The last reported wave, or null when none has run. `result` is null
     *  when the wave is suppressed — nothing of a withheld wave is published. */
    latestWave: z
        .object({
        code: ShortText,
        /** The wave's close instant (ISO 8601), as the trend datasets carry it. */
        date: z.string().datetime({ offset: true }),
        live: z.boolean(),
        suppressed: z.boolean(),
        result: LamaHierarchyResultSchema.nullable(),
    })
        .strict()
        .nullable(),
    /** Present only when BOTH sources have a result — and a suppressed wave
     *  has none, so nothing of a withheld wave leaks through a delta. */
    comparison: LamaHierarchyComparisonSchema.nullable(),
})
    .strict();
export const LamaPillarResultsOutputSchema = z
    .object({
    rows: z.array(z
        .object({
        pillar: ShortText,
        current: z.number().nullable(),
        previous: z.number().nullable(),
        delta: z.number().nullable(),
        spark: z.array(z.number()),
        /**
         * The category's score in EVERY reported wave, oldest first, with `null`
         * where that wave did not measure it (PB-191 Task 21).
         *
         * `spark` cannot express this: it is a bare number array built by
         * FILTERING absent waves out, so a category present in W1 and W3 but not
         * W2 arrives as two points and the sparkline draws them as consecutive.
         * That is interpolation by omission — the reader sees a smooth trend
         * across a gap that does not exist, and cannot tell it apart from a
         * category measured only twice.
         *
         * OPTIONAL and alongside `spark` rather than replacing it: the shape is
         * shared by two persisted dataset refs (`pillar-results@1` and
         * `category-results@2`), so changing `spark`'s type would break both.
         */
        history: z
            .array(z
            .object({
            code: ShortText,
            score: z.number().nullable(),
            /** True for the OPEN wave — still recomputing (§13.4). */
            live: z.boolean(),
        })
            .strict())
            .optional(),
        /** Always null — same PD5 reason as the profile's `target`. */
        target: z.number().nullable(),
    })
        .strict()),
    /** See {@link suppressedFlag}. `rows` is empty when true. */
    suppressed: suppressedFlag,
})
    .strict();
/**
 * Team × wave maturity. PERMANENTLY EMPTY in v1 and honestly so: the LAMA
 * delivery spine (campaign → wave → assignment → response) carries no team
 * dimension at all — assignments reference a respondent directory id, never a
 * team — so there is nothing to group by. Plan dependency PD5 owns adding one.
 * The shape stays in the contract so the widget and its connection need no
 * migration when it lands; the provider must NOT invent a grouping (org unit,
 * audience rule) to fill it.
 */
export const LamaTeamHeatmapOutputSchema = z
    .object({
    teams: z.array(ShortText),
    waveLabels: z.array(ShortText),
    /** rows[teamIndex][waveIndex]; null = team not measured in that wave. */
    rows: z.array(z.array(z.number().nullable())),
})
    .strict();
export const LamaTopMoversOutputSchema = z
    .object({
    improved: z.array(z
        .object({ area: ShortText, current: z.number(), delta: z.number() })
        .strict()),
    slipped: z.array(z
        .object({ area: ShortText, current: z.number(), delta: z.number() })
        .strict()),
    /**
     * Areas measured in BOTH waves whose score did not move (PB-191 Task 22).
     *
     * These used to vanish. `improved` filters `delta > 0` and `slipped`
     * `delta < 0`, so a question with a delta of exactly 0 fell into neither —
     * and the widget's "No areas changed" copy only appeared when BOTH lists were
     * empty, so three improvements plus five unchanged rendered as three
     * improvements and no sign the other five had been assessed at all. Holding
     * steady is a result, and an area silently dropped reads as an area not
     * measured.
     *
     * OPTIONAL, so this is not a version bump: strictly additive, and both
     * rollout orders stay safe — an old resolver omits the key and the widget
     * shows two columns as before. Distinct from an EMPTY array, which means
     * "nothing held steady".
     */
    unchanged: z
        .array(z
        .object({ area: ShortText, current: z.number(), delta: z.number() })
        .strict())
        /* The same 5 the projector slices to. DECLARED here because this is the
           only one of the three lists whose natural size follows the instrument
           rather than the movement: a campaign where nothing changed puts every
           assessed question in it. */
        .max(5)
        .optional(),
    /**
     * True when the report has exactly ONE wave. Both lists are then EMPTY by
     * construction, and that is the intended behaviour, not a gap to close:
     * the only comparison base available is the wave itself, so every delta
     * would be a tautological 0. The flag exists purely as a copy selector —
     * "this is the baseline wave" reads differently from "nothing moved". Do
     * not turn it into a self-comparison.
     */
    vsBaseline: z.boolean(),
    /** See {@link suppressedFlag}. Both lists are empty when true. */
    suppressed: suppressedFlag,
})
    .strict();
export const LamaPriorityRoadmapOutputSchema = z
    .object({
    entries: z.array(z
        .object({
        /**
         * The authored question this entry is about.
         *
         * Added so the improvement handoff can reference its SOURCE and so the
         * host can tell which findings already have work open. `area` cannot
         * serve as that key: it is the prompt TEXT, and duplicate prompts
         * within a version are legal — the report read model keys `byQuestion`
         * by id for exactly this reason. Joining on the text would attribute a
         * SOLVE to the wrong entry.
         *
         * Identifies an authored question, never a respondent, so it carries no
         * disclosure risk under ADR-0027 §5.
         *
         * Added as a required field on a `.strict()` schema; ADR-0054's
         * PB-4942 amendment records why this dataset stays at `@1` and what the
         * residual rolling-deploy exposure is.
         */
        questionId: ShortText,
        area: ShortText,
        pillar: ShortText,
        score: z.number(),
        /**
         * True when the latest wave does not measure this entry, so the
         * score shown is the curated one rather than a fresh reading.
         * Unreachable while membership is DERIVED from the latest wave (see
         * the provider's projection) — reachable the moment PD5 supplies a
         * curated roadmap store, which is the missing input, not a defect.
         */
        stale: z.boolean(),
    })
        .strict()),
    confidence: LamaConfidenceSchema,
    /** See {@link suppressedFlag}. `entries` is empty when true. */
    suppressed: suppressedFlag,
})
    .strict();
function dataset(ref, inputSchema, outputSchema) {
    const parsed = parseDatasetContractRef(ref);
    if (!parsed)
        throw new Error(`Invalid dashboard dataset contract ref '${ref}'.`);
    return [
        ref,
        {
            providerKey: parsed.providerKey,
            datasetKey: parsed.datasetKey,
            contractVersion: parsed.contractVersion,
            inputSchema,
            outputSchema,
        },
    ];
}
/**
 * Every dataset contract the v1 dashboard capability ships. Keys are the
 * canonical `providerKey/datasetKey@version` references that widget-contract
 * slots accept and connections persist (decomposed into provider_key /
 * dataset_key / contract_version columns).
 */
export const DASHBOARD_DATASET_CONTRACTS = new Map([
    dataset("kpi/kpi-snapshot@1", KpiSnapshotInputSchema, KpiSnapshotSchema),
    dataset("kpi/kpi-list@1", KpiListInputSchema, KpiListOutputSchema),
    dataset("solve/project-pulse@1", EmptyInputSchema, ProjectPulseOutputSchema),
    dataset("solve/my-tasks@1", EmptyInputSchema, MyTasksOutputSchema),
    dataset("solve/my-active-solves@1", EmptyInputSchema, MyActiveSolvesOutputSchema),
    /* The cross-SOLVE action read model. Its INPUT carries the whole filter, so
       unlike every other `solve/*` dataset it is a `bounded_parameters` binding
       rather than an empty one — the selection is what the editor persists. */
    dataset("solve/solve-actions@1", SolveActionsInputSchema, SolveActionsOutputSchema),
    dataset("vsm/my-visible-vsms@1", EmptyInputSchema, MyVisibleVsmsOutputSchema),
    dataset("hoshin/strategic-health@1", EmptyInputSchema, StrategicHealthOutputSchema),
    dataset("player-ai/daily-brief@1", EmptyInputSchema, DailyBriefOutputSchema),
    dataset("player-ai/coaching-nudge@1", EmptyInputSchema, CoachingNudgeOutputSchema),
    dataset("personal-state/mood-today@1", EmptyInputSchema, MoodTodayOutputSchema),
    dataset("vsm/linked-kpis@1", EmptyInputSchema, VsmLinkedKpisOutputSchema),
    dataset("vsm/linked-kpi@1", VsmLinkedKpiInputSchema, VsmLinkedKpiOutputSchema),
    dataset("kaizen/personal-list@3", PersonalKaizenListInputSchema, KaizenListOutputSchema),
    dataset("kaizen/vsm-list@3", VsmKaizenListInputSchema, KaizenListOutputSchema),
    // Health stays at `@2` on purpose. `KaizenHealthOutputSchema` is independent —
    // `overall` plus seven counts, with no reference to `KaizenRowSchema` — so its
    // output did not change. Bumping it would push every saved health slot into
    // `DASHBOARD_UNSUPPORTED_CONTRACT_VERSION` and demand an explicit migration,
    // for nothing.
    dataset("kaizen/vsm-health@2", EmptyInputSchema, KaizenHealthOutputSchema),
    dataset("lama/report-overview@1", EmptyInputSchema, LamaReportOverviewOutputSchema),
    dataset("lama/maturity-profile@1", EmptyInputSchema, LamaMaturityProfileOutputSchema),
    dataset("lama/maturity-trend@1", EmptyInputSchema, LamaMaturityTrendOutputSchema),
    dataset("lama/participation-trend@1", EmptyInputSchema, LamaParticipationTrendOutputSchema),
    /* PB-191 Task 2a: the pillar→category rename EXPANDS the dataset ref, for the
       same reason the widget kind expands (D35) — one layer down. A dataset ref is
       PERSISTED, not just code: `dashboard.widget_data_connections` stores
       provider_key/dataset_key/contract_version per widget instance. So renaming
       the ref alone would leave every existing report's connection row pointing at
       a ref the widget no longer accepts, and `slot.accepts.includes(ref)` is the
       FIRST gate in `dashboard-resolve.service.ts` — it returns INVALID_CONNECTION
       with `retryable: false`, an error state rather than an empty one. The shipped
       connection-migration registry cannot repair it either: it migrates
       `parameters` between versions of the SAME provider/dataset key and is
       hard-limited to single version steps, so it cannot rename a datasetKey.
  
       Both refs therefore resolve at once, and they deliberately serve the
       IDENTICAL output shape. The `pillar` payload field is renamed in a LATER
       release, once Task 4's connection-row rewrite has landed everywhere and only
       one ref is still live — otherwise two payload shapes would be reachable at
       the same time and the widget would need a two-shape read that a later
       cleanup has to find and remove. Retired with the kind alias at Task 33. */
    dataset("lama/pillar-results@1", EmptyInputSchema, LamaPillarResultsOutputSchema),
    dataset("lama/category-results@2", EmptyInputSchema, LamaPillarResultsOutputSchema),
    dataset("lama/team-heatmap@1", EmptyInputSchema, LamaTeamHeatmapOutputSchema),
    dataset("lama/top-movers@1", EmptyInputSchema, LamaTopMoversOutputSchema),
    dataset("lama/priority-roadmap@1", EmptyInputSchema, LamaPriorityRoadmapOutputSchema),
    dataset("lama/maturity-hierarchy@1", EmptyInputSchema, LamaMaturityHierarchyOutputSchema),
]);
export function getDashboardDatasetContract(ref) {
    return DASHBOARD_DATASET_CONTRACTS.get(ref);
}
//# sourceMappingURL=dashboard-datasets.js.map